import {http} from '../http'
import * as facesApi from '../../coreApi/faces'
import {APIResponse} from '../types'
import {
  PersonCloseCall,
  PersonCloseCallCoverage,
  PersonTaggedPhoto,
  PersonTaggingDiagnostics,
} from '../../../types'
import {handleError} from '../handleError'
import {
  RawPersonCloseCall,
  RawPersonCloseCallCoverage,
  RawPersonTaggedPhoto,
} from '../../coreApi/faces/types'

interface Params {
  personId: number
  clientId: number
  season?: number
}

type ResolvedReturn = APIResponse<PersonTaggingDiagnostics>

export interface ServiceMethod {
  (params: Params): Promise<ResolvedReturn>
}

const ERROR_PATH = 'api.faces.getPersonTaggingDiagnostics()'
const GENERIC_ERROR =
  'Something went wrong while loading tagging diagnostics. Please try again.'

// A 403 here has a specific, actionable meaning that the raw server message
// does not convey: either the caller is not a CampMinder employee, or their
// token is not scoped to the camp id they typed in. Exported so the view can
// render it verbatim without re-deriving the condition.
export const FORBIDDEN_ERROR =
  'You are not authorized to view diagnostics for that camp.'

/* eslint-disable @typescript-eslint/no-explicit-any */
const isForbidden = (e: any): boolean => e?.response?.status === 403
/* eslint-enable @typescript-eslint/no-explicit-any */

// Photo and album ids are interpolated straight into an album deep link, so the
// declared `number` type has to actually hold at runtime — a TypeScript
// annotation is not a guarantee about a JSON body. Anything that isn't a
// positive integer collapses to 0, which callers treat as "not linkable" and
// render unlinked (see isLinkableId). Without this, a version skew where the
// server still sends bare numeric ids instead of objects yields
// `/album/undefined?photo=undefined` and a thumbnail request for `undefined` —
// broken, silent, and indistinguishable from a permissions problem.
const toId = (value: unknown): number => {
  const id = Number(value)
  return Number.isInteger(id) && id > 0 ? id : 0
}

// Normalize the nullable fields at the decode boundary so a field omitted by
// the server can't leak through as `undefined` (which the null-vs-value UI
// branches don't expect). `?? null` keeps a real 0 intact while coalescing a
// missing value to null. Mirrors getRecognitionAttempts' decode.
const toCloseCall = (raw: RawPersonCloseCall): PersonCloseCall => ({
  photoId: toId(raw?.photoId),
  albumId: toId(raw?.albumId),
  albumName: raw?.albumName ?? '',
  confidence: raw.confidence ?? null,
  thresholdUsed: raw.thresholdUsed,
  attemptedUTC: raw.attemptedUTC,
  taggingPersonId: raw.taggingPersonId ?? null,
})

// Absent -> null (unknown), never zeros. Counts are coerced because they arrive
// from JSON: a string count would make '4' >= '10' compare true and silently
// suppress the incompleteness warning downstream.
const toCoverage = (
  raw: RawPersonCloseCallCoverage | undefined,
): PersonCloseCallCoverage | null => {
  if (!raw) return null

  return {
    manualTaggedPhotos: Number(raw.manualTaggedPhotos),
    manualTaggedWithTelemetry: Number(raw.manualTaggedWithTelemetry),
    // A faces that reports coverage but predates the candidate-scoped count
    // leaves this undefined. NaN propagates to `unknown` rather than being
    // silently read as "nothing was answerable".
    manualTaggedWithCandidateTelemetry: Number(
      raw.manualTaggedWithCandidateTelemetry,
    ),
  }
}

const toTaggedPhoto = (raw: RawPersonTaggedPhoto): PersonTaggedPhoto => ({
  photoId: toId(raw?.photoId),
  albumId: toId(raw?.albumId),
  albumName: raw?.albumName ?? '',
})

export const getPersonTaggingDiagnostics: ServiceMethod = async ({
  personId,
  clientId,
  season,
}) => {
  let resolvedReturn: ResolvedReturn

  try {
    // The faces service enforces the authoritative `campminderstaff` gate and
    // per-client authorization, returning 403 to unauthorized callers. The
    // client-side employee check is only a visibility affordance.
    const response = await facesApi.getPersonTaggingDiagnostics(http, {
      PersonID: personId,
      ClientID: clientId,
      Season: season,
    })

    if (!response || !response.data) {
      throw new Error('Unsuccessful Get Person Tagging Diagnostics')
    }

    const {data} = response

    resolvedReturn = {
      result: 'success',
      data: {
        personId: data.personId,
        clientId: data.clientId,
        training: data.training
          ? {
              trainedUTC: data.training.trainedUTC,
              firstTrainedUTC: data.training.firstTrainedUTC ?? null,
              enrolledFaceCount: data.training.enrolledFaceCount ?? 0,
            }
          : null,
        seasons: (data.seasons || []).map((s) => ({
          season: s.season,
          photosTagged: s.photosTagged,
          frTags: s.frTags,
          manualTags: s.manualTags,
        })),
        season: data.season ?? null,
        closeCalls: {
          accepted: (data.closeCalls?.accepted || []).map(toCloseCall),
          nearMiss: (data.closeCalls?.nearMiss || []).map(toCloseCall),
          // An absent block stays NULL — never defaulted to zeros.
          //
          // Zeros are not a neutral default here: manualTaggedPhotos === 0
          // asserts "this camper has no manual tags this season", which is a
          // STRONGER claim than saying nothing, is false whenever an older
          // faces omits the field during a rolling deploy, and can contradict
          // the season stats rendered on the same screen. `null` means unknown,
          // and the UI has a state for that.
          coverage: toCoverage(data.closeCalls?.coverage),
        },
        photos: {
          all: (data.photos?.all || []).map(toTaggedPhoto),
          fr: (data.photos?.fr || []).map(toTaggedPhoto),
          manual: (data.photos?.manual || []).map(toTaggedPhoto),
        },
      },
    }
  } catch (e) {
    const failure = handleError(ERROR_PATH, GENERIC_ERROR, e)
    resolvedReturn = isForbidden(e)
      ? {...failure, error: {...failure.error, message: FORBIDDEN_ERROR}}
      : failure
  }

  return Promise.resolve(resolvedReturn)
}
